import { ethers } from 'ethers';

const WORLD_CHAIN_RPC_URL = import.meta.env.VITE_WORLD_CHAIN_RPC_URL
  || 'https://worldchain-mainnet.g.alchemy.com/public';

export const ON_CHAIN_TOKEN_ADDRESSES = {
  BBG: import.meta.env.VITE_BBG_CONTRACT_ADDRESS
    || '0xF3B6C8DC909c91f305338Cd87dadE05240897cED',
  WLD: import.meta.env.VITE_WLD_CONTRACT_ADDRESS
    || '0x2cFc85d8E48F8EAB294be644d9E25C3030863003',
  BBT: import.meta.env.VITE_BBT_CONTRACT_ADDRESS
    || '0x5F2825B05646cA0389Ff022a4CADd1dD70F2c276',
  GGMOMOMO: import.meta.env.VITE_GGMOMOMO_CONTRACT_ADDRESS
    || '0x18C4B55ECE23404E0dB7794C25261Cd45a65f170',
} as const;

export const BBG_VAULT_CONTRACT_ADDRESS = import.meta.env.VITE_BBG_VAULT_CONTRACT_ADDRESS
  || '0x3B03A781586b5BA6873ac8dE90E4915311fE9DDF';
export const SHOP_ADDRESS = import.meta.env.VITE_BBG_FEE_RECIPIENT_ADDRESS
  || '0x76ebb5e7f7bf7254a26f118fd09f947805c9c6ef';
export const WLD_WITHDRAWAL_FEE_BASE_UNITS = '50000000000000000';

export type OnChainTokenSymbol = keyof typeof ON_CHAIN_TOKEN_ADDRESSES;

export interface OnChainTokenBalance {
  address: string;
  balance: string;
  symbol: string;
  name: string;
  decimals: number;
  raw: bigint;
  error: string | null;
}

export const worldChainProvider = new ethers.JsonRpcProvider(WORLD_CHAIN_RPC_URL, {
  chainId: 480,
  name: 'World Chain',
});

const ERC20_ABI = [
  'function balanceOf(address owner) view returns (uint256)',
  'function decimals() view returns (uint8)',
  'function symbol() view returns (string)',
  'function name() view returns (string)',
];

export async function getERC20Balance(
  contractAddress: string,
  walletAddress: string,
): Promise<OnChainTokenBalance> {
  const contract = new ethers.Contract(contractAddress, ERC20_ABI, worldChainProvider);
  const [balance, decimals, symbol, name] = await Promise.all([
    contract.balanceOf(walletAddress) as Promise<bigint>,
    contract.decimals() as Promise<number>,
    contract.symbol() as Promise<string>,
    contract.name() as Promise<string>,
  ]);

  return {
    address: contractAddress,
    balance: ethers.formatUnits(balance, decimals),
    symbol,
    name,
    decimals,
    raw: balance,
    error: null,
  };
}

export async function getOnChainBalances(
  walletAddress: string,
): Promise<Record<OnChainTokenSymbol, OnChainTokenBalance>> {
  const entries = await Promise.all(
    Object.entries(ON_CHAIN_TOKEN_ADDRESSES).map(async ([symbol, address]) => {
      try {
        const balance = await getERC20Balance(address, walletAddress);
        return [symbol, balance] as const;
      } catch (error) {
        const message = error instanceof Error ? error.message : '鏈上餘額查詢失敗';
        return [symbol, {
          address,
          balance: '0',
          symbol,
          name: symbol,
          decimals: 18,
          raw: 0n,
          error: message,
        }] as const;
      }
    }),
  );

  return Object.fromEntries(entries) as Record<OnChainTokenSymbol, OnChainTokenBalance>;
}

export function formatOnChainBalance(amount: string): string {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '—';
  if (value === 0) return '0.00';
  if (value < 0.0001) return '< 0.0001';
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(2)}K`;
  if (value >= 1) return value.toFixed(2);
  return value.toFixed(4);
}

export function formatOnChainTokenAmount(rawAmount: bigint, decimals: number, fractionDigits = 5): string {
  const formatted = ethers.formatUnits(rawAmount, decimals);
  const [integerPart, fractionPart] = formatted.split('.');
  const trimmedFraction = (fractionPart ?? '').slice(0, fractionDigits).replace(/0+$/, '');
  return trimmedFraction ? `${integerPart}.${trimmedFraction}` : integerPart;
}

export async function waitForOnChainTransaction(
  transactionHash: string,
): Promise<'confirmed' | 'failed'> {
  const receipt = await worldChainProvider.waitForTransaction(transactionHash, 1, 120_000);
  if (!receipt) {
    throw new Error('鏈上交易仍未確認，請稍後查看交易狀態。');
  }

  return receipt.status === 1 ? 'confirmed' : 'failed';
}
